import React from 'react';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {Button, Grid, TextField, Typography} from '@material-ui/core';
import {IFdCrop} from 'constants/interfaces';

export type Submission = {
  id?: number;
  cropId: string;
  numberOfHectaresFarmed: number;
  yieldPerHectare: number;
  year: number;
  farmId?: number;
  notes?: string;
};

export function FdProductionHistoryForm(props: {
  defaultValues?: Partial<Submission>[];
  isLoading: boolean;
  allCrops: IFdCrop[];
  submitButtonTitle: string;
  onSubmit: (submissions: Partial<Submission>[]) => void;
  cancelButtonTitle?: string;
  onCancel?: () => void;
  readonly?: boolean;
}) {
  const {handleSubmit, control} = useForm<{submissions: Partial<Submission>[]}>(
    {
      defaultValues: {
        submissions:
          props.defaultValues?.sort((a, b) => b.year! - a.year!) || [],
      },
    },
  );

  const {fields} = useFieldArray({
    control,
    name: 'submissions',
  });

  const onSubmit = (data: {submissions: Partial<Submission>[]}) => {
    props.onSubmit(data.submissions);
  };

  return (
    <div className="generic-form-container production-history-form">
      <div className="form-container">
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit)}
          style={{width: '100%'}}
        >
          {fields.map((field, index) => (
            <Grid container spacing={2} className="form-section" key={field.id}>
              <Grid item xs={12}>
                <Controller
                  name={`submissions.${index}.year`}
                  control={control}
                  render={({field: {value}}) => (
                    <Typography variant="subtitle2" gutterBottom>
                      {value}
                    </Typography>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`submissions.${index}.numberOfHectaresFarmed`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Number of Hectares Farmed"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value || ''}
                      onChange={(e) => onChange(parseFloat(e.target.value))}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly}
                    />
                  )}
                  rules={{required: 'Number of hectares farmed is required'}}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`submissions.${index}.yieldPerHectare`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Yield Per Hectare"
                      variant="outlined"
                      fullWidth
                      type="number"
                      value={value || ''}
                      onChange={(e) => onChange(parseFloat(e.target.value))}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly}
                    />
                  )}
                  rules={{required: 'Yield per hectare is required'}}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name={`submissions.${index}.notes`}
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      label="Notes"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      rowsMax={6}
                      value={value || ''}
                      onChange={onChange}
                      placeholder="Enter any additional notes…"
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={props.readonly}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ))}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {props.cancelButtonTitle && (
                <Button
                  className="secondary-btn"
                  variant="contained"
                  fullWidth
                  onClick={props.onCancel}
                >
                  {props.cancelButtonTitle}
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                className="primary-btn"
                type="submit"
                variant="contained"
                fullWidth
                color="primary"
              >
                {props.submitButtonTitle}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
